import { useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { Phone, Mail, Globe, MapPin, Loader2, Send } from "lucide-react";
import { CATEGORIES } from "@/data/services";
import { API } from "@/lib/scroll";

const INFO = [
  { icon: Phone, label: "Call Us", value: "+91 9619923347", href: "tel:+919619923347", testid: "contact-phone-link" },
  { icon: Mail, label: "Email", value: "info@arkitenterprises.in", href: "mailto:info@arkitenterprises.in", testid: "contact-email-link" },
  { icon: Globe, label: "Website", value: "www.arkitenterprises.in", href: "https://www.arkitenterprises.in", testid: "contact-website-link" },
];

const EMPTY = { name: "", company: "", email: "", phone: "", category: "", message: "" };

export const Contact = () => {
  const [form, setForm] = useState(EMPTY);
  const [sending, setSending] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(`${API}/enquiries`, form);
      toast.success("Enquiry received — our engineers will reach out within one business day.");
      setForm(EMPTY);
    } catch (err) {
      toast.error("Could not send your enquiry. Please call us at +91 9619923347.");
    } finally {
      setSending(false);
    }
  };

  const inputCls =
    "w-full bg-ark-800/70 border border-white/10 rounded-xl px-4 py-3.5 text-sm text-slate-200 placeholder:text-slate-500 focus:outline-none focus:border-cyan-400/50 focus:shadow-[0_0_20px_rgba(56,189,248,0.1)] transition-all duration-300";

  return (
    <section id="contact" data-testid="contact-section" className="relative py-28 lg:py-36 bg-ark-900/40 border-t border-white/5">
      <div className="max-w-7xl mx-auto px-6 grid lg:grid-cols-12 gap-14">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.2 }}
          transition={{ duration: 0.8 }}
          className="lg:col-span-5"
        >
          <p className="font-mono text-xs tracking-[0.3em] text-cyan-400 uppercase font-semibold">
            06 // Initiate
          </p>
          <h2 className="mt-4 font-display font-bold tracking-tight text-2xl sm:text-3xl lg:text-4xl text-slate-100 leading-tight">
            Request an IT Audit or <span className="metallic-text">Instant Quote</span>
          </h2>
          <p className="mt-5 text-base text-slate-400 leading-relaxed">
            Tell us what you need — procurement, deployment, security testing or a
            full AMC — and our team will respond with a plan and pricing.
          </p>

          <div className="mt-10 space-y-4">
            {INFO.map((item) => (
              <a
                key={item.label}
                data-testid={item.testid}
                href={item.href}
                target={item.href.startsWith("http") ? "_blank" : undefined}
                rel="noreferrer"
                className="flex items-center gap-4 p-4 rounded-xl border border-white/10 bg-ark-800/50 hover:border-cyan-400/40 hover:bg-ark-700/50 transition-all duration-300 group"
              >
                <span className="w-10 h-10 rounded-full bg-cyan-400/10 border border-cyan-400/25 flex items-center justify-center shrink-0">
                  <item.icon size={16} className="text-cyan-300" />
                </span>
                <span>
                  <span className="block font-mono text-[10px] tracking-[0.25em] text-slate-500 uppercase">
                    {item.label}
                  </span>
                  <span className="block text-sm text-slate-200 group-hover:text-white transition-colors">
                    {item.value}
                  </span>
                </span>
              </a>
            ))}
            <div className="flex items-start gap-4 p-4 rounded-xl border border-white/10 bg-ark-800/50">
              <span className="w-10 h-10 rounded-full bg-cyan-400/10 border border-cyan-400/25 flex items-center justify-center shrink-0">
                <MapPin size={16} className="text-cyan-300" />
              </span>
              <span>
                <span className="block font-mono text-[10px] tracking-[0.25em] text-slate-500 uppercase">
                  Head Office
                </span>
                <span className="block text-sm text-slate-300 leading-relaxed">
                  Menz House, Ground Floor, Sector-11, Juhu Village, Vashi, Thane,
                  Navi Mumbai, Maharashtra 400703
                </span>
              </span>
            </div>
            <div className="rounded-xl overflow-hidden border border-white/10 shadow-[0_20px_50px_-20px_rgba(0,0,0,0.6)] hover:border-cyan-400/30 transition-colors duration-300">
              <iframe
                data-testid="contact-map-embed"
                title="ARK IT ENTERPRISES head office — Vashi, Navi Mumbai"
                src="https://maps.google.com/maps?q=Menz%20House%2C%20Sector%2011%2C%20Juhu%20Village%2C%20Vashi%2C%20Navi%20Mumbai%2C%20Maharashtra%20400703&t=&z=15&ie=UTF8&iwloc=&output=embed"
                className="w-full h-[280px] grayscale-[0.35] contrast-[1.05] opacity-90"
                loading="lazy"
                allowFullScreen
                referrerPolicy="no-referrer-when-downgrade"
              />
            </div>
          </div>
        </motion.div>

        <motion.form
          data-testid="enquiry-form"
          onSubmit={submit}
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.2 }}
          transition={{ duration: 0.8, delay: 0.15 }}
          className="lg:col-span-7 p-7 sm:p-10 rounded-3xl border border-white/10 bg-ark-800/40 backdrop-blur-sm"
        >
          <div className="grid sm:grid-cols-2 gap-5">
            <div>
              <label className="block font-mono text-[10px] tracking-[0.25em] text-slate-500 uppercase mb-2">Full Name *</label>
              <input data-testid="enquiry-name-input" required value={form.name} onChange={set("name")} placeholder="Your name" className={inputCls} />
            </div>
            <div>
              <label className="block font-mono text-[10px] tracking-[0.25em] text-slate-500 uppercase mb-2">Company Name</label>
              <input data-testid="enquiry-company-input" value={form.company} onChange={set("company")} placeholder="Organization" className={inputCls} />
            </div>
            <div>
              <label className="block font-mono text-[10px] tracking-[0.25em] text-slate-500 uppercase mb-2">Email Address *</label>
              <input data-testid="enquiry-email-input" type="email" required value={form.email} onChange={set("email")} placeholder="you@company.com" className={inputCls} />
            </div>
            <div>
              <label className="block font-mono text-[10px] tracking-[0.25em] text-slate-500 uppercase mb-2">Phone Number *</label>
              <input data-testid="enquiry-phone-input" required value={form.phone} onChange={set("phone")} placeholder="+91 ..." className={inputCls} />
            </div>
            <div className="sm:col-span-2">
              <label className="block font-mono text-[10px] tracking-[0.25em] text-slate-500 uppercase mb-2">Service Category *</label>
              <select data-testid="enquiry-category-select" required value={form.category} onChange={set("category")} className={`${inputCls} appearance-none`}>
                <option value="" disabled>Select a chapter…</option>
                {CATEGORIES.map((c) => (
                  <option key={c.id} value={c.name} className="bg-ark-900">
                    {c.id} — {c.name}
                  </option>
                ))}
                <option value="Other / Not sure" className="bg-ark-900">Other / Not sure</option>
              </select>
            </div>
            <div className="sm:col-span-2">
              <label className="block font-mono text-[10px] tracking-[0.25em] text-slate-500 uppercase mb-2">Requirement Details *</label>
              <textarea data-testid="enquiry-message-input" required rows={4} value={form.message} onChange={set("message")} placeholder="Describe your project, site count, timelines…" className={`${inputCls} resize-none`} />
            </div>
          </div>
          <button
            data-testid="enquiry-form-submit-btn"
            type="submit"
            disabled={sending}
            className="mt-8 w-full sm:w-auto inline-flex items-center justify-center gap-3 px-9 py-4 rounded-full font-display font-semibold text-ark-950 bg-gradient-to-r from-slate-100 via-slate-300 to-slate-400 hover:shadow-[0_0_35px_rgba(56,189,248,0.4)] hover:scale-[1.02] disabled:opacity-60 disabled:cursor-not-allowed transition-all duration-300"
          >
            {sending ? <Loader2 size={17} className="animate-spin" /> : <Send size={16} />}
            {sending ? "Transmitting…" : "Submit Enquiry"}
          </button>
        </motion.form>
      </div>
    </section>
  );
};
